import * as React from 'react';
import * as ReactDom from 'react-dom';
import { instance } from '@lib/index';
import {
  getRoutes,
  initAPI,
  MenuDataInfo,
  Routes,
  USER_CACHE_KEY,
  ORG_CACHE_KEY,
  AGENT_CACHE_KEY,
  API
} from '@config/index';
import {
  UserProps,
  OrgInfo,
  AgentInfo,
  UserContext,
  AgentContext,
  OrgContext
} from '@interface/index';
import { AppContainer } from 'react-hot-loader';
import { isEqual } from 'lodash';
import { message } from 'antd';
import { Provider } from 'react-redux';
import { store, persistor } from '@reducer/index';
import { globalModel } from '@reducer/global';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { history, RouteComp } from '@route/route';
import { K_LOCALE_UPDATED } from '@locale/index';
import { getOrgId, getAgentId, setMasterAgentInfo, getAgentInfo } from '@lib/quick-func';
import '@style/index.less';
import { getCopilotInstance, setCopilotUser } from './copilot';

import 'vanilla-cookieconsent/dist/cookieconsent.css';
import * as CookieConsent from 'vanilla-cookieconsent';

const copilot = getCopilotInstance();

const icon = require('@static/favicon.ico');

const link = document.createElement('link');

link.setAttribute('rel', 'shortcut icon');
link.setAttribute('href', icon);
link.setAttribute('type', 'image/x-icon');
document.head.append(link);


const isDev = process.env.NODE_ENV === 'development';

interface HomePropsInfo { }
interface HomeStateInfo {
  userData: UserProps;
  org?: OrgInfo;
  agent?: AgentInfo;
}


class Home extends React.Component<HomePropsInfo, HomeStateInfo> {
  state: HomeStateInfo = {
    userData: {}
  };

  routes: JSX.Element[] = [];

  constructor(props: HomePropsInfo) {
    super(props);

    initAPI();

    const orgCache = window.sessionStorage.getItem(ORG_CACHE_KEY);
    const org = orgCache && orgCache !== 'undefined' && JSON.parse(orgCache) || undefined;
    const agentCache = window.sessionStorage.getItem(AGENT_CACHE_KEY);
    const agent = agentCache && JSON.parse(agentCache);
    const userCache = window.localStorage.getItem(USER_CACHE_KEY);
    const userData = userCache && JSON.parse(userCache);

    this.state = {
      org,
      agent,
      userData
    };
  }

  getRoutes = () => {

    if (!this.routes || !this.routes.length) {
      Routes.map((item: MenuDataInfo) => {
        getRoutes(item, '', this.routes, true);
      });
    }

    return this.routes;
  }

  componentDidMount() {

    this.initCookieConsent();

    // update prev location pathname
    let prePathname = window.location.pathname;
    history.listen(async ({ location, action }) => {

      const { userData } = this.state;
      const userCache = window.localStorage.getItem(USER_CACHE_KEY);

      if (!userCache) {
        this.updateUserData();
        return;
      }

      const user = JSON.parse(userCache);
      if (user.user_id !== userData.user_id) {
        this.updateUserData(user);
      }

      await store.dispatch({
        type: 'global/update_state',
        payload: {
          from: prePathname
        }
      });

      prePathname = location.pathname + location.search;
    });

    message.config({
      maxCount: 2
    });

    instance.on('update_language', () => {
      this.setState({});
    });

    instance.on(K_LOCALE_UPDATED, () => {
      this.setState({});
    });
  }

  initCookieConsent() {
    CookieConsent.run({

      categories: {
        necessary: {
          enabled: true,  // this category is enabled by default
          readOnly: true  // this category cannot be disabled
        },
        analytics: {}
      },

      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies',
              description: 'We use cookies and other tracking technologies to improve your browsing experience on our website, to show you personalized content, to analyze our website traffic, and to understand where our visitors are coming from.',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Accept only necessary cookies'
              // showPreferencesBtn: 'Manage Individual preferences'
            },
            preferencesModal: {
              title: 'Manage cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Accept current selection',
              closeIconLabel: 'Close modal',
              sections: [
                {
                  title: 'Somebody said ... cookies?',
                  description: 'I want one!'
                },
                {
                  title: 'Strictly Necessary cookies',
                  description: 'These cookies are essential for the proper functioning of the website and cannot be disabled.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Performance and Analytics',
                  description: 'These cookies collect information about how you use our website. All of the data is anonymized and cannot be used to identify you.',
                  linkedCategory: 'analytics'
                },
                {
                  title: 'More information',
                  description: 'For any queries in relation to my policy on cookies and your choices, please <a href="#contact-page">contact us</a>'
                }
              ]
            }
          }
        }
      }
    });

    // CookieConsent.show(true);
  }

  componentWillUnmount() {
    instance.off('update_language');
    instance.off(K_LOCALE_UPDATED);
  }

  handleOrg = (org?: OrgInfo, callback?: () => void) => {
    const old = this.state.org;
    if (!org) {
      window.sessionStorage.removeItem(ORG_CACHE_KEY);
      setMasterAgentInfo();
      copilot.clearHistory();
      window.localStorage.setItem(`show-copilot-${this.state.userData.user_id}`, 'false');
      this.setState({ org: undefined, agent: undefined }, callback);
      return;
    }

    window.sessionStorage.setItem(ORG_CACHE_KEY, JSON.stringify(org || ''));

    if (org.copilotEnabled && org.copilotBotUrl) {
      copilot.setUrl(org.copilotBotUrl);

      if (!old || old.id !== org.id) {
        copilot.clearHistory();
        window.localStorage.setItem(`show-copilot-${this.state.userData.user_id}`, 'false');
      }
    }

    this.setState({
      org
    }, callback);
  }

  handleAgent = (agent?: AgentInfo, org?: OrgInfo) => {
    if (!agent) {
      setMasterAgentInfo();
      window.sessionStorage.removeItem(AGENT_CACHE_KEY);

      store.dispatch(
        globalModel.effects.fetchParameters({ id: '' })
      );
    } else {
      const cache = window.sessionStorage.getItem(AGENT_CACHE_KEY);
      const oldAgent = this.state.agent || (cache && JSON.parse(cache));
      store.dispatch(
        globalModel.effects.fetchParameters({ id: agent.id, forceUpdate: !this.state.agent || agent.id !== oldAgent.id })
      );
      window.sessionStorage.setItem(AGENT_CACHE_KEY, JSON.stringify(agent));
    }

    if (org) {
      window.sessionStorage.setItem(ORG_CACHE_KEY, JSON.stringify(org));
      this.setState({
        agent,
        org
      }, () => {
        if (agent && agent.id) {
          setTimeout(() => {
            API.common.initStructUpdate();
          }, 5000);
        }
      });
    } else {
      this.setState({
        agent
      }, () => {
        if (agent && agent.id) {
          setTimeout(() => {
            API.common.initStructUpdate();
          }, 5000);
        }
      });
    }
  };

  updateUserData = (info?: UserProps) => {

    if (!info || isEqual(info, {})) {
      window.localStorage.removeItem(USER_CACHE_KEY);
      this.setState({ userData: {} });
      return;
    }

    const userData = this.formatUserInfo(info);

    const oldInfo = this.state.userData;

    if (!isEqual(userData, oldInfo)) {
      window.localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
      this.setState({
        userData,
        agent: undefined
      }, () => {

        setCopilotUser({
          id: info.user_id || '',
          name: info.nickname || '',
        });

        if ((!oldInfo || !oldInfo.user_id || !oldInfo.user_id.length) && userData.user_id.length || (oldInfo.user_id !== userData.user_id)) {
          copilot.clearHistory();
          window.localStorage.setItem(`show-copilot-${userData.user_id}`, 'false');
        }

      });
    }
  };

  formatUserInfo = (info: UserProps) => ({
    area_code: info.area_code,
    account: info.account,
    headimgurl: info.headimgurl || '',
    nickname: info.nickname || '',
    role: info.role || '',
    user_id: info.user_id || '',
    company: info.company || '',
    email: info.email || '',
    email_verify: info.email_verify || false,
    state: info.state || ''
  });

  render() {
    const state = this.state;

    return (
      <Provider store={store}>
        <PersistGate loading={undefined} persistor={persistor}>
          <UserContext.Provider
            value={{
              user: state.userData,
              handleUser: this.updateUserData,
              formatUserInfo: this.formatUserInfo
            }}
          >
            <OrgContext.Provider
              value={{
                org: state.org,
                handleOrg: this.handleOrg
              }}
            >
              <AgentContext.Provider
                value={{
                  agent: state.agent,
                  handleAgent: this.handleAgent
                }}
              >
                {RouteComp({
                  userData: state.userData,
                  routes: this.getRoutes(),
                  updateUserData: this.updateUserData
                })}
              </AgentContext.Provider>
            </OrgContext.Provider>
          </UserContext.Provider>
        </PersistGate>
      </Provider>
    );
  }
}

if (isDev && (module as any).hot) {
  (module as any).hot.accept(() => {
    ReactDom.render(
      <AppContainer>
        <Home />
      </AppContainer>,
      document.getElementById('content')
    );
  });
}

ReactDom.render(
  <AppContainer>
    <Home />
  </AppContainer>,
  document.getElementById('content')
);

